// Sprint F5: bloco "Rateio por setor" (% canônico + R$ derivado).
// 30/09/2026 (Lucas: "o botão salvar deve servir para todas as alterações
// realizadas na parcela"): dentro do modal da parcela este card NÃO salva mais
// sozinho — ele só edita, e quem grava é o "Salvar alterações" do rodapé. Com
// dois botões, editar aqui e salvar lá (ou o contrário) jogava metade fora.
// O modo com `onPct` é o controlado; sem ele, o card segue salvando sozinho
// (nenhuma outra tela usa hoje, mas a porta fica fechada por contrato).
// FZ 0.3 (2026-07-03): ganhou MODO DE EDIÇÃO — inputs de % por setor com
// validação soma=100% e salvar via fin_atualizar_conta_receber. É o % por
// área da empresa que discrimina a receita na DRE (relato do Lucas).
// Aceita rateio em qualquer formato (proporção ou R$ absoluto) — converte
// pra proporção localmente. Persiste sempre canônico (proporção, 4 casas).
import { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { SETORES } from './utils';
import { semearRateioPct } from './parcelaForm';

export function RateioParcela({ rateio, valorPrevisto, parcelaId, onMudou, pct: pctFora, onPct }: {
  rateio: Record<string, number> | undefined | null;
  valorPrevisto: number;
  parcelaId?: string;
  onMudou?: () => void;
  /** Controlado pelo modal: % em edição (null = ninguém está editando). */
  pct?: Record<string, string> | null;
  /** Controlado pelo modal: o card só avisa; o Salvar do rodapé é que grava. */
  onPct?: (pct: Record<string, string> | null) => void;
}) {
  const controlado = typeof onPct === 'function';
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // Percentuais em edição (0..100, 1 casa), chaveados pelo setor
  const [pctLocal, setPctLocal] = useState<Record<string, string>>({});
  const pct = controlado ? (pctFora || {}) : pctLocal;
  const setPct = (novo: Record<string, string>) => {
    if (controlado) onPct!(novo); else setPctLocal(novo);
  };
  const emEdicao = controlado ? pctFora != null : editando;

  // Normaliza qualquer formato → proporção (espelha normalizarRateio do backend)
  const pares = Object.entries(rateio || {}).filter(([, v]) => typeof v === 'number' && v > 0);
  const totalRaw = pares.reduce((s, [, v]) => s + v, 0);
  const jaProporcao = totalRaw > 0.95 && totalRaw < 1.05;
  const proporcoes = pares.map(([k, v]) => ({
    chave: k,
    rotulo: SETORES[k]?.rotulo || k,
    cor: SETORES[k]?.cor || '#888',
    ordem: SETORES[k]?.ordem || 99,
    proporcao: totalRaw > 0 ? (jaProporcao ? v : (v / totalRaw)) : 0,
  })).sort((a, b) => a.ordem - b.ordem);

  const valor = Number(valorPrevisto) || 0;
  const somaProp = proporcoes.reduce((s, p) => s + p.proporcao, 0);
  const fora100 = proporcoes.length > 0 && Math.abs(somaProp - 1) > 0.01;

  function abrirEdicao() {
    // A semente é a MESMA régua que o modal usa para decidir se houve
    // alteração (parcelaForm.semearRateioPct) — duas contas diferentes aqui
    // fariam o Salvar regravar um rateio que ninguém tocou.
    setPct(semearRateioPct(rateio, Object.keys(SETORES)));
    setEditando(true);
  }
  function fecharEdicao() {
    // O `editando` local também volta no modo controlado: é ele que faz o
    // "✏️ Editar" reaparecer no cabeçalho. Sem isso, Cancelar escondia o
    // link para sempre e só fechando o modal dava para editar de novo.
    setEditando(false);
    if (controlado) onPct!(null);
  }

  const somaEdicao = Object.values(pct)
    .reduce((s, v) => s + (parseFloat(String(v).replace(',', '.')) || 0), 0);
  const edicaoOk = Math.abs(somaEdicao - 100) <= 0.1;

  async function salvar() {
    if (!parcelaId || !edicaoOk) return;
    setSalvando(true);
    try {
      const novo: Record<string, number> = {};
      for (const [k, v] of Object.entries(pct)) {
        const n = parseFloat(String(v).replace(',', '.')) || 0;
        if (n > 0) novo[k] = Math.round((n / 100) * 10000) / 10000;
      }
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_atualizar_conta_receber', id: parcelaId, rateio_setores: novo },
        { headers: { Authorization: token } });
      setEditando(false);
      onMudou?.();
    } catch (e: any) {
      alert('Erro ao salvar rateio: ' + (e.response?.data?.error || e.message));
    } finally {
      setSalvando(false);
    }
  }

  const cabecalho = (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
      <label style={{ flex: 1, fontSize: 10, fontWeight: 600, color: '#555',
        textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Rateio por setor
      </label>
      {parcelaId && !emEdicao && (
        <button type="button" onClick={abrirEdicao}
          style={{ fontSize: 11, border: 'none', background: 'transparent',
            color: '#0930D0', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
          ✏️ Editar
        </button>
      )}
    </div>
  );

  if (emEdicao) {
    return (
      <div>
        {cabecalho}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6,
          padding: 10, background: '#FAFBFF', border: '0.5px solid #B7C5E8', borderRadius: 5 }}>
          {Object.entries(SETORES).sort((a, b) => a[1].ordem - b[1].ordem).map(([k, s]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: s.cor, flexShrink: 0 }} />
              <span style={{ color: '#444', flex: 1 }}>{s.rotulo}</span>
              <input value={pct[k] ?? ''} onChange={e => setPct({ ...pct, [k]: e.target.value })}
                placeholder="0" inputMode="decimal"
                style={{ width: 64, padding: '3px 6px', fontSize: 12, textAlign: 'right',
                  border: '0.5px solid #B7C5E8', borderRadius: 4 }} />
              <span style={{ color: '#888', fontSize: 11 }}>%</span>
            </div>
          ))}
          {controlado && (
            <div style={{ fontSize: 10.5, color: '#666', marginTop: 2 }}>
              Grava no <b>Salvar alterações</b> do rodapé, junto com o resto da parcela.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4,
            paddingTop: 6, borderTop: '0.5px solid #d8dcec', fontSize: 12 }}>
            <span style={{ flex: 1, fontWeight: 600,
              color: edicaoOk ? '#0a7f4a' : '#B91626' }}>
              Soma: {somaEdicao.toFixed(1)}% {edicaoOk ? '✓' : '(precisa fechar 100%)'}
            </span>
            <button type="button" onClick={fecharEdicao} disabled={salvando}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4,
                border: '0.5px solid #ccc', background: '#fff', cursor: 'pointer' }}>
              Cancelar
            </button>
            {/* Dentro do modal da parcela não há Salvar aqui: grava no rodapé. */}
            {!controlado && (
              <button type="button" onClick={salvar} disabled={!edicaoOk || salvando}
                style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, fontWeight: 600,
                  border: '0.5px solid #0a7f4a', background: edicaoOk ? '#0a7f4a' : '#ccc',
                  color: '#fff', cursor: edicaoOk ? 'pointer' : 'not-allowed' }}>
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (proporcoes.length === 0) {
    return (
      <div>
        {cabecalho}
        <div style={{ padding: 10, background: '#FAFBFF', border: '0.5px dashed #d8dcec',
          borderRadius: 5, fontSize: 11, color: '#888', fontStyle: 'italic' }}>
          Sem rateio por setor cadastrado.
        </div>
      </div>
    );
  }

  return (
    <div>
      {cabecalho}
      {/* Barra segmentada */}
      <div style={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden',
        background: '#eee', marginBottom: 8 }}>
        {proporcoes.map(p => (
          <div key={p.chave} style={{
            background: p.cor, width: `${p.proporcao * 100}%`,
          }} title={`${p.rotulo}: ${(p.proporcao * 100).toFixed(1)}%`} />
        ))}
      </div>
      {/* Lista detalhada */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {proporcoes.map(p => {
          const valorSetor = valor * p.proporcao;
          return (
            <div key={p.chave} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: p.cor, flexShrink: 0 }} />
              <span style={{ color: '#444', flex: 1 }}>{p.rotulo}</span>
              <span style={{ fontWeight: 600, color: '#222', minWidth: 56, textAlign: 'right' }}>
                {(p.proporcao * 100).toFixed(1)}%
              </span>
              <span style={{ color: '#666', minWidth: 100, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                R$ {valorSetor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          );
        })}
      </div>
      {/* Rodapé: total */}
      <div style={{ marginTop: 8, paddingTop: 6, borderTop: '0.5px solid #eee',
        display: 'flex', alignItems: 'center', gap: 8, fontSize: 11,
        color: fora100 ? '#B91626' : '#888' }}>
        <span style={{ flex: 1, fontWeight: fora100 ? 600 : 400 }}>
          {fora100 ? '⚠ Soma ≠ 100% (rateio inconsistente)' : 'Total'}
        </span>
        <span style={{ fontWeight: 600, minWidth: 56, textAlign: 'right' }}>
          {(somaProp * 100).toFixed(1)}%
        </span>
        <span style={{ color: '#666', minWidth: 100, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          R$ {(valor * somaProp).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );
}
